import React from 'react';
import { Dropdown } from 'antd';
import {
  HomeOutlined,
  HomeFilled,
  RadarChartOutlined,
  FormOutlined,
  AppstoreOutlined,
  AppstoreFilled,
  LogoutOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { User } from '../types';
import { brand, neutral } from '../theme/tokens';
import { FEATURE_FLAGS } from '../featureFlags';

const tabs = [
  { key: '/home', icon: <HomeOutlined />, activeIcon: <HomeFilled />, label: '首页' },
  { key: '/home/profile', icon: <RadarChartOutlined />, activeIcon: <RadarChartOutlined />, label: '成长画像' },
  { key: '/home/services', icon: <AppstoreOutlined />, activeIcon: <AppstoreFilled />, label: '家校服务' },
  // 测评入口受 FEATURE_FLAGS.ENABLE_ASSESSMENT 控制，关闭时整个 tab 不渲染
  ...(FEATURE_FLAGS.ENABLE_ASSESSMENT
    ? [{ key: '/home/assessment', icon: <FormOutlined />, activeIcon: <FormOutlined />, label: '测评' }]
    : []),
];

// 家校服务的子路由，激活时高亮“家校服务”tab
const SERVICE_PATHS = ['/home/services', '/home/courses', '/home/leave', '/home/chain', '/home/album', '/home/mailbox', '/home/family', '/home/brain', '/home/schedule'];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user: User = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const activeKey = (() => {
    const p = location.pathname;
    if (SERVICE_PATHS.some((sp) => p === sp || p.startsWith(sp + '/'))) return '/home/services';
    if (p === '/home') return '/home';
    if (p.startsWith('/home/profile')) return '/home/profile';
    if (p.startsWith('/home/assessment')) return '/home/assessment';
    return '/home';
  })();

  const userMenu = {
    items: [
      {
        key: 'password',
        icon: <LockOutlined />,
        label: '修改密码',
        onClick: () => navigate('/change-password'),
      },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
    ],
  };

  return (
    <div style={{ minHeight: '100vh', background: neutral[50], paddingBottom: 72 }}>
      {/* 顶部极简 header：校徽 + 名字 + 退出 */}
      <header
        style={{
          height: 52, background: neutral[0], borderBottom: `1px solid ${neutral[100]}`,
          display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10,
          position: 'sticky', top: 0, zIndex: 50,
        }}
      >
        <img src="/school-badge.gif" alt="校徽" style={{ width: 30, height: 30, objectFit: 'contain' }} />
        <span style={{ fontSize: 15, fontWeight: 600, color: neutral[800], flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          成长指导平台
        </span>
        <Dropdown menu={userMenu} trigger={['click']}>
          <span style={{ fontSize: 13, color: neutral[600], cursor: 'pointer' }}>{user.name} ▾</span>
        </Dropdown>
      </header>

      <Outlet />

      {/* 底部导航 */}
      <nav
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          borderTop: `1px solid ${neutral[100]}`,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          padding: '6px 0 env(safe-area-inset-bottom, 6px)', zIndex: 100,
          boxShadow: '0 -2px 12px rgba(15,23,42,0.04)',
        }}
      >
        {tabs.map((tab) => {
          const active = activeKey === tab.key;
          return (
            <div
              key={tab.key}
              onClick={() => navigate(tab.key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                cursor: 'pointer', padding: '4px 12px', minWidth: 56,
                color: active ? brand[600] : neutral[400], transition: 'color 0.2s',
              }}
            >
              <span style={{ fontSize: 21, lineHeight: 1 }}>{active ? tab.activeIcon : tab.icon}</span>
              <span style={{ fontSize: 11, marginTop: 3, fontWeight: active ? 600 : 400 }}>{tab.label}</span>
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default AppLayout;
